import { GLOBAL_PROJECT_TYPE_CHAMBER } from "../types";
import SubProject from "../sub-project";
import MultibedSelectors from "../../reducers/multi-bed-reducer";
import BackgroundSelectors from "../../reducers/background-reducer";

/**
 * Specific shared services for Multibed
 * Functions should be kept clean and pure, avoiding side effects or data mutation
 * @type {{validateOutletOverIsolator: (function(*, *, *): boolean), testSelectedChambersBeforeRemoval: (function(*, *): boolean), validateInternalOutletOnPerimeterRow(*, *, *): boolean, validateIsolatorOverOutlet: (function(*, *, *): boolean), validateSelectedChamberIsActive: (function(*, *): boolean), getActualRows: (function(*, *=, *=, *=, *=, *, *): *), handleManifoldBuildRestrictions: (function(*=, *=): null), removeSelectedChambers: (function(*, *, *): boolean), getChamberCapAccessory: (function(*=, *=, *): ({id: null}|*)), validateInnerFreeSpace: (function(*, *, *, *=, *): boolean), ignoreAccessoryAttachment: (function(*=, *): boolean)}}
 */
const MultiBedService = {
    /**
     * Will reset Settings
     * @requirements
     * @param currentBedName
     * @param props [projectType, stoneBedPoints,chamberbedX, chamberbedY, pipeBedPosition, chamberbedRotation,
     *    pipeBedRotation, chamberbedHeight, chamberbedWidth, pipeBedDimensions, chamberbedScaleX, chamberbedScaleY] on mapStateToProps
     *    && [getScaledValue()] on mapDispatchToProps
     * @example
     *         projectType: state.globals.projectType,
     *         stoneBedPoints: state.stonebed.points,
     *         chamberbedX: state.chamberbed.x,
     *         chamberbedY: state.chamberbed.y,
     *         pipeBedPosition: state.pipeBed.position,
     *         chamberbedRotation: state.chamberbed.rotation,
     *         pipeBedRotation: state.pipeBed.rotation,
     *         chamberbedHeight: state.chamberbed.height,
     *         chamberbedWidth: state.chamberbed.width,
     *         pipeBedDimensions: state.pipeBed.dimensions,
     *         chamberbedScaleX: state.chamberbed.scaleX,
     *         chamberbedScaleY: state.chamberbed.scaleY,
     * @returns {*}
     */
    getPlaceholderSettings: ( currentBedName, props ) => {
        try {
            if (!currentBedName || !props) throw new Error("Please provide a name and this.props object");
            const isChamber = props.projectType === GLOBAL_PROJECT_TYPE_CHAMBER;
            let result = {
                bedId: null,
                bedName: currentBedName,
                points: isChamber
                    ? props.stoneBedPoints
                    : null,
                x: isChamber
                    ? props.chamberbedX
                    : props.pipeBedPosition.x,
                y: isChamber
                    ? props.chamberbedY
                    : props.pipeBedPosition.y,
                rotation: isChamber
                    ? props.chamberbedRotation
                    : props.pipeBedRotation,
                height: isChamber
                    ? ( props.chamberbedHeight
                        ? props.chamberbedHeight
                        : Math.abs(props.stoneBedPoints[2] - props.stoneBedPoints[3]) )
                    : props.getScaledValue(props.pipeBedDimensions.length.ft),
                width: isChamber
                    ? ( props.chamberbedWidth
                        ? props.chamberbedWidth
                        : Math.abs(props.stoneBedPoints[0] - props.stoneBedPoints[1]) )
                    : props.getScaledValue(props.pipeBedDimensions.width.ft),
            };

            if (isChamber) {
                result.scaleX = props.chamberbedScaleX;
                result.scaleY = props.chamberbedScaleY;
            }

            return result;
        } catch (error) {
            console.error("Error on MultiBedService.getPlaceholderSettings() :: ", error.message);
        }
    },

    getMultibedDataForPDF: async ( props ) => {

        const pdfBeds = [...MultibedSelectors.getAllBeds()];

        let oid = props.user.id;
        let blobToken = props.resourcesTokens.projects.blobToken;

        for (const bed of pdfBeds) {
            const subProjectInstanceSilent = new SubProject(bed.id);
            const silentState = await subProjectInstanceSilent
                .fetchState(
                    oid,
                    blobToken,
                );

            if (( silentState["calculations"] ))
                bed["calculations"] = silentState["calculations"];

            if (( silentState["globals"] ))
                bed["globals"] = silentState["globals"];

            if (( silentState["pipeBed"] ))
                bed["pipeBed"] = silentState["pipeBed"];

            if (( silentState["pipeGlobals"] ))
                bed["pipeGlobals"] = silentState["pipeGlobals"];

            bed["state"] = silentState;
        }
        return pdfBeds;
    },

    /**
     * \
     * @param type
     * @param payloadObj
     * @param props
     * @returns {Promise<string|boolean>}
     */
    async syncMultibedAutomationData( type, payloadObj, props ) {
        try {
            if (props.isMultiBedProject) {
                const syncBeds = [...MultibedSelectors.getAllBeds()];
                let oid = props.user.id;
                let blobToken = props.resourcesTokens.projects.blobToken;
                const currentImg = BackgroundSelectors.getBackgroundObj();
                for (const bed of syncBeds) {
                    const subProjectInstanceSilent = new SubProject(bed.id);
                    const silentAutomation = await subProjectInstanceSilent
                        .fetchAutomation(
                            oid,
                            blobToken,
                        );

                    let updateAutomation = true;
                    switch (type) {
                        case "projectName":
                            silentAutomation.email.projectname = payloadObj.projectName;
                            console.log(`projectName : ${payloadObj.projectName}, silentAutomation.email.projectname : ${silentAutomation.email.projectname}`);
                            break;
                        case "background":
                            silentAutomation.background = payloadObj.background;
                            break;
                        case "syncAll":
                            // silentAutomation.email.projectname = payloadObj.projectName;
                            if (currentImg)
                                silentAutomation.background = currentImg;
                            break;
                        default:
                            console.warn(`Type not provided or incorrect ( ${type} )`);
                            console.warn(` No update done! )`);
                            updateAutomation = false;
                            break;
                    }

                    if (updateAutomation)
                        await subProjectInstanceSilent
                            .persistAutomationState(
                                silentAutomation,
                                props.user.id,
                                props.resourcesTokens.projects.blobToken,
                            );
                }
                return true;
            }
            return false;
        } catch (e) {
            console.log(`Error on MultiBedService.syncMultibedAutomationData() :: ${e.message}`);
            return "false";
        }
    },

    /**
     * Wil
     * @returns {Promise<boolean>}
     */
    async syncBedsData( bedsArray, props, otherUser = null ) {
        try {
            let oid = props.multiBedOtherUserId
                ? props.multiBedOtherUserId
                : props.user.id;
            let token = props.multiBedOtherUserId
                ? props.multiBedOtherUserTokens.subprojects.iTableToken
                : props.resourcesTokens["subprojects"]["tableToken"];

            let blobToken = props.multiBedOtherUserId
                ? props.multiBedOtherUserTokens.projects.iBlobToken
                : props.resourcesTokens["projects"]["blobToken"];

            if (otherUser && otherUser.otherUserId) {
                oid = otherUser.otherUserId;
                token = otherUser.otherUserTokens.subprojects.iTableToken;
                blobToken = otherUser.otherUserTokens.projects.iBlobToken;
            }

            let fetchParams = {
                oid, token, blobToken,
            };

            for (const bed of bedsArray) {
                let subProjectInstance = new SubProject(bed.id);
                const subProjectState = await subProjectInstance
                    .fetchState(
                        fetchParams.oid,
                        fetchParams.blobToken,
                    );
                bed.volume = subProjectState.pipeBed.volume;
            }
            props.updatedSelectedBedValues(bedsArray);
            return true;
        } catch (e) {
            console.error(`MultiBedService.syncBedsData() : ${e.message}`);
            return false;
        }
    },
};
export default MultiBedService;



// WEBPACK FOOTER //
// ./src/lib/services/multibed-service.js